import { useEffect, useState } from "react";
import { useAuthContext } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { fetchUser } from "../api/UserApi";
import { clearAuthTokens } from "../lib/authUtils";
import { captureException, identifyUser } from "../lib/posthog";

export const useAuthCheck = () => {
  const [isChecking, setIsChecking] = useState(true);
  const { dispatch } = useAuthContext();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Get current session from Supabase
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error("Auth check error:", error);
          captureException(error, { source: "useAuthCheck", step: "getSession" });
          setIsChecking(false);
          return;
        }

        if (session && session.user) {
          // User is authenticated, fetch additional details
          try {
            const userDetailsResponse = await fetchUser({
              queryKey: [null, session.user.id],
            });
            const userDetails = userDetailsResponse.data;

            dispatch({
              type: "LOGIN",
              payload: {
                isAuth: true,
                token: session.access_token,
                user_id: session.user.id,
                group_id: userDetails?.group_id ?? null,
                organisation_id: userDetails?.organisation_id ?? null,
                role: userDetails?.role ?? null,
              },
            });

            identifyUser({
              user_id: session.user.id,
              group_id: userDetails?.group_id ?? null,
              organisation_id: userDetails?.organisation_id ?? null,
            });

            // Update localStorage
            localStorage.setItem(
              "userData",
              JSON.stringify({
                id: session.user.id,
                group_id: userDetails?.group_id ?? null,
                organisation_id: userDetails?.organisation_id ?? null,
                token: session.access_token,
                device_token: userDetails?.device_token ?? null,
                role: userDetails?.role ?? null,
              })
            );
          } catch (userDetailsError) {
            console.error("Error fetching user details:", userDetailsError);
            captureException(userDetailsError, {
              source: "useAuthCheck",
              step: "fetchUserDetails",
            });
            // Still set basic auth if user details fetch fails
            dispatch({
              type: "LOGIN",
              payload: {
                isAuth: true,
                token: session.access_token,
                user_id: session.user.id,
                group_id: null,
                organisation_id: null,
              },
            });
            identifyUser({
              user_id: session.user.id,
              group_id: null,
              organisation_id: null,
            });
          }
        } else {
          // No session, clear any stored data
          clearAuthTokens();
          dispatch({
            type: "LOGOUT",
            payload: null,
          });
        }
      } catch (err) {
        console.error("Auth check error:", err);
        captureException(err, { source: "useAuthCheck", step: "outer" });
        // Clear stored data on error
        clearAuthTokens();
        dispatch({
          type: "LOGOUT",
          payload: null,
        });
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [dispatch]);

  return { isChecking };
};
